import { relative } from 'node:path';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import { isLoggedInToAccount } from '@wix/cli-auth';
import { createHttpClient } from '@wix/cli-http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { ProjectModel } from '../model.js';
import { extensionHandlers } from '../components/GenerateCommand/extensionHandlers.js';
import type { GenerateExtensionType } from '../components/GenerateCommand/extensionHandlers.js';
import { extensionTypeSchema } from '../components/GenerateCommand/generate-schema/types.js';

interface GenerateSuccess {
  extensionType: string;
  newFiles: string[];
}

async function assertAuth() {
  const authData = await isLoggedInToAccount();
  if (!authData) {
    throw new CliError({
      code: CliErrorCode.AuthenticationRequired(),
      cause: null,
    });
  }
  return authData;
}

function parseExtensionType(
  params: Record<string, unknown>
): GenerateExtensionType {
  const extensionTypeResult = extensionTypeSchema.safeParse(params);

  if (!extensionTypeResult.success) {
    throw new Error(
      `'extensionType' must be specified. Run 'wix schema generate' to see available types.`
    );
  }

  const { extensionType } = extensionTypeResult.data;

  if (!Object.hasOwn(extensionHandlers, extensionType)) {
    throw new Error(
      `Unknown extension type: '${extensionType}'. Run 'wix schema generate' to see available types.`
    );
  }

  return extensionType as GenerateExtensionType;
}

export async function nonInteractiveGenerateExtension(
  _services: AstroCommandServices,
  model: ProjectModel,
  params: Record<string, unknown>
): Promise<GenerateSuccess> {
  const authData = await assertAuth();
  const extensionType = parseExtensionType(params);

  const handler = extensionHandlers[extensionType];

  const httpClient = createHttpClient({
    type: 'backoffice',
    getAppToken: () => authData.getAccessToken({}),
  });

  const zodSchema =
    typeof handler.zodSchema === 'function'
      ? await handler.zodSchema(httpClient)
      : handler.zodSchema;

  const result = zodSchema.safeParse(params);

  if (!result.success) {
    throw new Error(
      result.error.errors
        .map((e) =>
          e.path.length > 0 ? `${e.path.join('.')}: ${e.message}` : e.message
        )
        .join('\n')
    );
  }

  if (handler.validate) {
    const validationError = await handler.validate(
      model.projectFolder,
      result.data
    );
    if (validationError) {
      throw new Error(validationError);
    }
  }

  const generationResult = await handler.generate({
    projectFolder: model.projectFolder,
    appId: model.config.appId,
    model,
    data: result.data,
    httpClient,
  });

  return {
    extensionType,
    newFiles: (generationResult?.newFiles ?? []).map((f: string) =>
      relative(model.projectFolder, f)
    ),
  };
}
